import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {documentosPublicosApi} from '../../../api/api';
import type {DocumentoAdjuntoDTO} from '../../../api/Objetos';

type PublicImageScope = 'LOGO_PROYECTO' | 'GALERIA_PROYECTO';

export function PublicProjectMedia({proyectoId}: {proyectoId: number}) {
    const logos = usePublicImages(proyectoId, 'LOGO_PROYECTO');
    const gallery = usePublicImages(proyectoId, 'GALERIA_PROYECTO');
    const [selected, setSelected] = React.useState<PublicImage | null>(null);

    return (
        <>
            {logos.length ? (
                <section className="projectLogoStrip" aria-label="Logotipos institucionales y de financiación">
                    {logos.map(image => <img key={image.id} src={image.url} alt={image.name}/>) }
                </section>
            ) : null}
            {gallery.length ? (
                <section className="projectGallery" aria-labelledby="project-gallery-title">
                    <div className="sectionHeading"><h2 id="project-gallery-title">GALERÍA DE IMÁGENES</h2></div>
                    <div className="projectGalleryGrid">
                        {gallery.map(image => (
                            <button key={image.id} type="button" onClick={() => setSelected(image)} aria-label={`Ampliar ${image.name}`}>
                                <img src={image.url} alt={image.name}/>
                            </button>
                        ))}
                    </div>
                </section>
            ) : null}
            {selected ? (
                <div className="imageLightbox" role="dialog" aria-modal="true" aria-label={selected.name} onClick={() => setSelected(null)}>
                    <button type="button" className="imageLightboxClose" onClick={() => setSelected(null)} aria-label="Cerrar">×</button>
                    <img src={selected.url} alt={selected.name}/>
                </div>
            ) : null}
        </>
    );
}

type PublicImage = {id: number; name: string; url: string};

function usePublicImages(proyectoId: number, scope: PublicImageScope): PublicImage[] {
    const query = useQuery({
        queryKey: ['public', 'project-images', proyectoId, scope],
        queryFn: () => documentosPublicosApi.listar(proyectoId, scope),
    });
    const [images, setImages] = React.useState<PublicImage[]>([]);

    React.useEffect(() => {
        let active = true;
        const urls: string[] = [];
        Promise.all((query.data ?? []).map(async (document: DocumentoAdjuntoDTO) => {
            const blob = await documentosPublicosApi.contenido(proyectoId, document.id!);
            const url = URL.createObjectURL(blob);
            urls.push(url);
            return {id: document.id!, name: document.nombre || document.nombreArchivo, url};
        })).then(result => { if (active) setImages(result); }).catch(() => { if (active) setImages([]); });
        return () => {
            active = false;
            urls.forEach(url => URL.revokeObjectURL(url));
        };
    }, [proyectoId, query.data]);
    return images;
}
